import React, { type ReactNode, useEffect, useMemo } from "react";
import { useLocation } from "@gatsbyjs/reach-router";
import { useIsClient } from "@/hooks/useIsClient";
import { I18nextProvider } from "react-i18next";
import browserLang from "browser-lang";
import { getTimezone } from "countries-and-timezones";
import { withPrefix } from "gatsby";
import { LOCALE_KEY, LOCALES, DEFAULT_LOCALE } from "../appConfig";
import { LocaleApplied } from "context/LocaleApplied";
import { Shell } from "@/features/shell/components/Shell";
import { useRouteDock } from "@/features/shell/hooks/useDock";
import { resolveBackButton } from "@/features/shell/utils/backButton";
import type { NavLocation } from "@/features/shell/types";
import { ErrorBoundary } from "@/features/shell/components/ErrorBoundary";
import { BridgeEventHandler } from "@/features/bridge/components/BridgeEventHandler";
import { AuthHooks } from "@/features/auth/components/AuthHooks";
import { BrazeInitializer } from "@/features/braze/components/BrazeInitializer";
import { VerificationGate } from "@/features/account/components/verification/VerificationGate";
import { useGlobalWindowSize } from "@/hooks/events/useWindowSize";
import { navigate } from "@/utilities/navigate";
import { useHomeUrl } from "@/hooks/firestore/user/usePreferences";
import { PageContext } from "context/Page";
import getI18nInstance from "utilities/i18n";
import enFallback from "../locales/en/translation.json";
import enAuFallback from "../locales/en-AU/translation.json";
import WithScripts from "gatsby/WithScripts";
import type { PageContext as CreatedPageContext } from "gatsby/createPages";
import localObject from "utilities/localObject";
import type { i18n, Resource } from "i18next";
import {
  type LocaleValue,
  validLocales,
  resolveLinkLocale,
  stripLocalePrefix,
} from "@/utilities/locale";
import { parse } from "query-string";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { shouldForceAuLocale } from "@/utilities/shouldForceAuLocale";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import { LoaderIcon, PageLoader } from "@picklebet/ui";
import * as styles from "./wrapPageElement.module.scss";

const LocaleSync = ({ i18n, locale }: { i18n: i18n; locale: string }) => {
  const forceAu = shouldForceAuLocale();

  // NZ has no localized URL, so force AU translations (Multi, not Parlay).
  const target = forceAu ? "en-AU" : locale;
  useEffect(() => {
    if (i18n.language !== target) {
      void i18n.changeLanguage(target);
    }
  }, [i18n, target]);
  return null;
};

const I18nWrapper = ({
  i18n,
  localeApplied,
  children,
}: {
  i18n: i18n;
  localeApplied: boolean;
  children: React.ReactNode;
}) => (
  <LocaleApplied.Provider value={localeApplied}>
    <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
  </LocaleApplied.Provider>
);

const ShellRoute = ({
  location,
  children,
}: {
  location: NavLocation;
  children: ReactNode;
}) => {
  const pathnameWithoutLocale = stripLocalePrefix(location.pathname);
  const dock = useRouteDock();
  const { key } = useLocation();
  const canGoBack = !!key && key !== "initial";

  return (
    <Shell
      location={{ pathname: pathnameWithoutLocale, search: location.search }}
      options={{
        dock,
        backButton: canGoBack && resolveBackButton(pathnameWithoutLocale),
      }}
    >
      {children}
    </Shell>
  );
};

const getTzLanguage = (): LocaleValue | null => {
  if (typeof window === "undefined" || typeof Intl === "undefined") {
    return null;
  }

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tz = getTimezone(userTimeZone);
  const country = tz?.countries?.[0];

  if (country === "AU") {
    return "en-au";
  } else if (country === "CA") {
    return "en-ca";
  } else if (country === "US") {
    return "en-us";
  }

  return null;
};

const getDetectedLocale = (): string => {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE.toLowerCase();
  }

  const [storedLocale] = localObject.getValue<LocaleValue>(LOCALE_KEY);

  return (
    storedLocale ||
    getTzLanguage() ||
    browserLang({
      languages: validLocales,
      fallback: DEFAULT_LOCALE.toLowerCase(),
    }) ||
    DEFAULT_LOCALE.toLowerCase()
  ).toLowerCase();
};

const buildResources = (
  nodes: { node: { data: string; language: string; ns: string } }[],
): Resource =>
  nodes.reduce<Record<string, Record<string, any>>>((acc, { node }) => {
    (acc[node.language] ??= {})[node.ns] = JSON.parse(node.data);
    return acc;
  }, {});

const validLocalePattern = new RegExp(
  `^/(${LOCALES.map((v) => v.toLowerCase()).join("|")})/`,
);
const defaultLocale = DEFAULT_LOCALE.toLowerCase();

const RedirectLocale = ({
  children,
  i18n,
  props,
}: {
  children: ReactNode;
  i18n: i18n;
  props: {
    pageContext: {
      hasTranslations: boolean;
      locale: string;
      section: string;
    };
    data: any;
    location: Location;
  };
}) => {
  const {
    pageContext: { hasTranslations, locale },
    location: { pathname, search = "" },
  } = props;

  const { homeUrl, isLoading } = useHomeUrl();
  const isClient = useIsClient();
  useGlobalWindowSize();

  const pathnameWithoutLocale = pathname.replace(validLocalePattern, "/");
  const pageLocale = locale.toLowerCase();
  const detectedLocale = getDetectedLocale();
  const isNotStream = pathname.indexOf("stream") === -1;
  const isHome = pathnameWithoutLocale === "/";

  const isLogin = pathnameWithoutLocale.startsWith("/login");
  const isSignUp = pathnameWithoutLocale.startsWith("/register");
  const isLiveChat = pathnameWithoutLocale.startsWith("/live-chat");

  const goHomeWithIntent = isLogin || isSignUp || isLiveChat;

  const targetPath = isHome && homeUrl ? homeUrl : pathnameWithoutLocale;

  const targetLocale = resolveLinkLocale(targetPath, detectedLocale);

  const needsRedirect = useMemo(
    () =>
      goHomeWithIntent ||
      isHome ||
      (isNotStream && hasTranslations && targetLocale !== pageLocale),
    [
      goHomeWithIntent,
      isHome,
      isNotStream,
      hasTranslations,
      targetLocale,
      pageLocale,
    ],
  );

  const prefix = targetLocale === defaultLocale ? "" : `/${targetLocale}`;
  const newUrl = withPrefix(`${prefix}${targetPath}${search}`);
  const setModal = useShellStore((state) => state.setModal);

  const isLoggedIn = useAuthStore(
    (s) => s.status === "authenticated" || s.status === "impersonating",
  );
  const isAuthLoading = useAuthStore((s) => s.status === "loading");

  useEffect(() => {
    if (!needsRedirect) {
      return;
    }

    if (goHomeWithIntent) {
      if (isAuthLoading || isLoading || !homeUrl) {
        return;
      }

      const affiliateLink = parse(search)["r"];
      const targetUrl = affiliateLink
        ? `${homeUrl}/?r=${affiliateLink}`
        : homeUrl;

      if (isLiveChat) {
        useHelpCenterStore.getState().requestChat();
      } else if (!isLoggedIn) {
        setModal(isLogin ? "signIn" : "signUp");
      }

      navigate(targetUrl, false, { replace: true });
      return;
    }

    if (isHome && isLoading) {
      return;
    }

    navigate(newUrl, true, { replace: true });
  }, [
    needsRedirect,
    goHomeWithIntent,
    isAuthLoading,
    isLoading,
    homeUrl,
    isLoggedIn,
    isLogin,
    isLiveChat,
    search,
    isHome,
    newUrl,
  ]);

  return (
    <I18nWrapper i18n={i18n} localeApplied={!needsRedirect}>
      <LocaleSync i18n={i18n} locale={locale} />
      <PageContext.Provider value={props.pageContext}>
        <ErrorBoundary>
          <AuthHooks />
          <BridgeEventHandler />
          <BrazeInitializer />
          <VerificationGate>
            <ShellRoute location={props.location}>
              {isClient ? children : <PageLoader />}
            </ShellRoute>
          </VerificationGate>
        </ErrorBoundary>
        {isClient && needsRedirect && (
          <div className={styles.overlay}>
            <div className={styles.icon}>
              <LoaderIcon />
            </div>
          </div>
        )}
      </PageContext.Provider>
    </I18nWrapper>
  );
};

export const wrapPageElement = ({
  element,
  props,
}: {
  element: React.ReactNode;
  props: {
    pageContext: {
      hasTranslations: boolean;
      locale: string;
      section: string;
      shell?: CreatedPageContext["shell"];
    };
    data: any;
    location: Location;
  };
}) => {
  const {
    hasTranslations: hasPageTranslations,
    locale,
    section,
    shell,
  } = props.pageContext;

  const { data } = props;
  const localeNodes = data?.locales?.edges || [];
  const i18n = getI18nInstance();

  if (typeof window !== "undefined" && locale) {
    const detectedLocale = getDetectedLocale();

    window.___currentLocale = locale.toLowerCase();
    window.___detectedLocale = detectedLocale;
    window.___defaultLocale = DEFAULT_LOCALE.toLowerCase();
    window.___defaultSection = "betting";
    window.___hasTranslations = hasPageTranslations;
    window.___country = locale.split("-")[1];
  }

  if (!i18n.isInitialized) {
    i18n.init({
      // NZ has no localized URL, so init straight to en-AU (Multi, not Parlay)
      // and avoid a first-paint flash before LocaleSync's changeLanguage runs.
      lng: shouldForceAuLocale() ? "en-AU" : locale,
      fallbackLng: "en",
      defaultNS: "translation",
      fallbackNS: false,
      keySeparator: "<.>",
      react: {
        useSuspense: false,
      },
      resources: buildResources(localeNodes),
    });
  } else {
    localeNodes.forEach(
      ({ node }: { node: { data: string; language: string; ns: string } }) => {
        if (!i18n.hasResourceBundle(node.language, node.ns)) {
          i18n.addResourceBundle(node.language, node.ns, JSON.parse(node.data));
        }
      },
    );
  }

  if (!i18n.hasResourceBundle("en", "translation")) {
    i18n.addResourceBundle("en", "translation", enFallback);
  }

  if (!i18n.hasResourceBundle("en-AU", "translation")) {
    i18n.addResourceBundle("en-AU", "translation", enAuFallback);
  }

  const elementsWithScripts = <WithScripts>{element}</WithScripts>;

  if (section && shell !== "bare") {
    return (
      <RedirectLocale i18n={i18n} props={props}>
        {elementsWithScripts}
      </RedirectLocale>
    );
  }

  return (
    <I18nWrapper i18n={i18n} localeApplied={true}>
      <LocaleSync i18n={i18n} locale={locale} />
      {elementsWithScripts}
    </I18nWrapper>
  );
};
